import React, { useState, useCallback, useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group, Slider, Checkbox, Alert, Divider, Text, Collapse } from '@mantine/core';
import { IconDeviceFloppy, IconX, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import { UpdateBusinessCommand } from '@/domain/entities/Business';
import { BusinessDetailDto } from '@/domain/entities/Business';
import { useUpdateBusinessMutation } from '@/application/queries/businessQueries';
import { useAuth } from '../../../hooks/useAuth';

export interface EditBusinessModalProps {
  isOpen: boolean;
  business: BusinessDetailDto | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * EditBusinessModal - Modal for editing an existing business
 *
 * Features:
 * - Pre-filled form with current business data
 * - Only editable fields (name, category, description, contact info)
 * - Form validation
 * - Success/error notifications via mutation hook
 * - Loading state during save
 *
 * Note: Region cannot be changed after creation (immutable relationship)
 */
export const EditBusinessModal: React.FC<EditBusinessModalProps> = ({
  isOpen,
  business,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const updateBusinessMutation = useUpdateBusinessMutation();

  // Form state
  const [formData, setFormData] = useState<UpdateBusinessCommand>({
    id: '',
    name: '',
    category: '',
    businessType: '',
    description: '',
    phone: '',
    website: '',
    email: '',
    openingHours: '',
    autoNotifyConfidenceThreshold: null, // SESSION_028: null = use system default (85%)
    matchOnAliasesOnly: false // SESSION_028: false = allow exact name matching
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Initialize form with business data when modal opens
   */
  useEffect(() => {
    if (isOpen && business) {
      setFormData({
        id: business.id,
        name: business.name,
        category: business.category,
        businessType: business.businessType ?? undefined,
        description: business.description ?? '',
        phone: business.phone ?? undefined,
        website: business.website ?? '',
        email: business.email ?? undefined,
        openingHours: business.openingHours ?? undefined,
        autoNotifyConfidenceThreshold: business.autoNotifyConfidenceThreshold ?? null, // SESSION_028
        matchOnAliasesOnly: business.matchOnAliasesOnly ?? false // SESSION_028
      });
      setErrors({});
    }
  }, [isOpen, business]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback(
    (field: keyof UpdateBusinessCommand, value: string): void => {
      setFormData((prev) => ({
        ...prev,
        [field]: value === '' ? undefined : value
      }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Business name is required
    if (!formData.name || formData.name.trim() === '') {
      newErrors['name'] = 'Business name is required';
    } else if (formData.name.length > 200) {
      newErrors['name'] = 'Business name must be 200 characters or less';
    }

    // Category is required
    if (!formData.category || formData.category.trim() === '') {
      newErrors['category'] = 'Category is required';
    }

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Invalid email format';
    }

    // Website validation (if provided)
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors['website'] = 'Website must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!business) {
      return;
    }

    if (!validate()) {
      return;
    }

    try {
      await updateBusinessMutation.mutateAsync({
        id: formData.id,
        command: formData,
        userRole: user?.role ?? 'BusinessOwner',
        userBusinessId: user?.businessId ?? null
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Update business failed:', error);
    }
  }, [business, formData, validate, updateBusinessMutation, user, onClose, onSuccess]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  if (!business) {
    return <></>;
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={`Edit Business: ${business.name}`}
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Business Name (Required) */}
        <TextInput
          label="Business Name"
          placeholder="Enter business name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.currentTarget.value)}
          error={errors['name']}
          required
          withAsterisk
        />

        {/* Category (Required) */}
        <TextInput
          label="Category"
          placeholder="e.g., Restaurant, Retail, Services"
          value={formData.category}
          onChange={(e) => handleChange('category', e.currentTarget.value)}
          error={errors['category']}
          required
          withAsterisk
        />

        {/* Business Type (Optional) */}
        <TextInput
          label="Business Type"
          placeholder="e.g., Fast Food, Cafe, Boutique"
          value={formData.businessType ?? ''}
          onChange={(e) => handleChange('businessType', e.currentTarget.value)}
        />

        {/* Description (Optional) */}
        <Textarea
          label="Description"
          placeholder="Describe the business"
          value={formData.description ?? ''}
          onChange={(e) => handleChange('description', e.currentTarget.value)}
          error={errors['description']}
          rows={3}
          maxLength={500}
        />

        {/* Contact Information */}
        <TextInput
          label="Phone"
          placeholder="(555) 123-4567"
          value={formData.phone ?? ''}
          onChange={(e) => handleChange('phone', e.currentTarget.value)}
        />

        <TextInput
          label="Website"
          placeholder="https://example.com"
          value={formData.website ?? ''}
          onChange={(e) => handleChange('website', e.currentTarget.value)}
          error={errors['website']}
        />

        <TextInput
          label="Email"
          placeholder="contact@business.com"
          type="email"
          value={formData.email ?? ''}
          onChange={(e) => handleChange('email', e.currentTarget.value)}
          error={errors['email']}
        />

        {/* Opening Hours */}
        <Textarea
          label="Opening Hours"
          placeholder="Mon-Fri: 9am-5pm"
          value={formData.openingHours ?? ''}
          onChange={(e) => handleChange('openingHours', e.currentTarget.value)}
          rows={2}
        />

        {/* SESSION_028: Matching Configuration Section */}
        <Divider my="md" label="Matching Configuration" labelPosition="center" />

        <Stack gap="md">
          {/* Confidence Threshold Slider */}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Auto-Notify Confidence Threshold
            </Text>
            <Text size="xs" c="dimmed">
              Only send automated notifications for mentions with confidence ≥ this threshold.
              Leave at default (85%) for most businesses.
            </Text>
            <Slider
              min={0}
              max={99}
              step={5}
              value={formData.autoNotifyConfidenceThreshold ?? 85}
              onChange={(value) => {
                const newValue = value === 85 ? null : value; // 85 = default, so store as null
                setFormData((prev) => ({ ...prev, autoNotifyConfidenceThreshold: newValue }));
              }}
              marks={[
                { value: 75, label: '75%' },
                { value: 85, label: '85% (default)' },
                { value: 95, label: '95%' }
              ]}
              label={(value) => `${value}%`}
            />
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Current: {formData.autoNotifyConfidenceThreshold ?? 85}%
                {formData.autoNotifyConfidenceThreshold === null && ' (system default)'}
              </Text>
              {formData.autoNotifyConfidenceThreshold !== null && formData.autoNotifyConfidenceThreshold !== 85 && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setFormData((prev) => ({ ...prev, autoNotifyConfidenceThreshold: null }))}
                >
                  Reset to default
                </Button>
              )}
            </Group>
          </Stack>

          {/* Match-On-Aliases-Only Checkbox */}
          <Checkbox
            label="Match only on business aliases (skip exact name matching)"
            description="Use this for businesses with generic names (e.g., 'Always', 'Home') to prevent false positives. Requires aliases to be configured."
            checked={formData.matchOnAliasesOnly ?? false}
            onChange={(e) => setFormData((prev) => ({ ...prev, matchOnAliasesOnly: e.currentTarget.checked }))}
          />

          {/* Warning Alert when MatchOnAliasesOnly is enabled */}
          {formData.matchOnAliasesOnly && (
            <Alert icon={<IconAlertTriangle size={16} />} title="Exact name matching disabled" color="yellow">
              This business will ONLY match on aliases. Make sure you've configured aliases with sufficient context
              (e.g., "Always Thai", "Always Restaurant"). Without aliases, this business will not match any mentions.
            </Alert>
          )}

          {/* Help Section */}
          <Collapse in={true}>
            <Alert icon={<IconInfoCircle size={16} />} title="When should I use these settings?" color="blue" variant="light">
              <Stack gap="xs">
                <div>
                  <Text size="sm" fw={500}>Higher Confidence Threshold (90-95%):</Text>
                  <Text size="xs">
                    Use for businesses with somewhat common names. Example: "Home Restaurant" might set 90% to avoid
                    matching generic "home" references.
                  </Text>
                </div>
                <div>
                  <Text size="sm" fw={500}>Match Only on Aliases:</Text>
                  <Text size="xs">
                    Use for businesses with very generic names. Example: Business named "Always" should enable this
                    and create aliases like "Always Thai" or "Always Restaurant" to match only meaningful mentions.
                  </Text>
                </div>
              </Stack>
            </Alert>
          </Collapse>
        </Stack>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSubmit}
            loading={updateBusinessMutation.isPending}
            disabled={updateBusinessMutation.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
